import React from 'react';
import PropTypes from 'prop-types';
import Tooltip from 'react-bootstrap/lib/Tooltip';
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import { Link } from 'react-router';
import Icon from '../../Icon';
import Button from '../../Button';
import { formatClassName } from '../../../Util';
import i18n from '../../../i18n/translate';

const ActionButton = props => {
  const { action, actionType, className, disabled, download, icon,
    iconFamily, iconTransform, keyField, row = {}, showLabel, target, title = '',
    tooltip, type } = props;

  const renderButtonWithTooltip = renderedButton => {
    const tooltipId = `tooltip-${title.toLowerCase()}-${row[keyField]}`

    return (
      <OverlayTrigger
        placement="top"
        delay={250}
        overlay={<Tooltip id={tooltipId}>{i18n.t(tooltip)}</Tooltip>}
      >
        {renderedButton}
      </OverlayTrigger>
    );
  }

  const renderButtonChildren = () => {
    return (
      <>
        <Icon fontFamily={iconFamily} transform={iconTransform} iconType={icon} />
        {showLabel && <span className="action-label">{i18n.t(title)}</span>}
      </>
    )
  }

  const preventClickThrough = event => { event && event.stopPropagation(); } // eslint-disable-line no-unused-expressions
  const preventAction = event => { event.preventDefault(); }

  const renderButton = () => {
    const buttonChildren = renderButtonChildren();
    const dataAttributes = {
      'data-action': (icon || type) ? `${icon || type}-${row.type}` : '',
      'data-object-id': row && row.id,
      'data-object-type': row && row.type
    };
    const commonButtonProps = {
      style: 'link',
      icon,
      iconFamily,
      label: showLabel ? i18n.t(title) : null,
      className: formatClassName(['m-r-10', className]),
      disabled
    };

    let renderedButton;
    switch (actionType) {
    case 'link':
      renderedButton = (
        <Link
          {...dataAttributes}
          className={formatClassName(['btn', 'btn-link', { disabled }, className])}
          download={download}
          onClick={disabled ? preventAction : preventClickThrough}
          target={target}
          to={action}
        >
          {buttonChildren}
        </Link>
      );
      break;
    case 'externalLink':
      renderedButton = (
        <Button
          {...dataAttributes}
          {...commonButtonProps}
          onClick={preventClickThrough}
          href={action}
          target={target}
          download={download}
        />
      );
      break;
    case 'callToAction':
      renderedButton = (
        <Button
          {...dataAttributes}
          {...commonButtonProps}
          onClick={e => { preventClickThrough(e); action(e, row); }}
        />
      );
      break;
    default:
      return null;
    }

    return tooltip ?
      renderButtonWithTooltip(renderedButton) :
      renderedButton;
  };

  return renderButton();
};

ActionButton.propTypes = {
  action: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
    .isRequired,
  actionType: PropTypes.oneOf([
    'link', 'externalLink', 'callToAction'
  ]).isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  download: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  icon: PropTypes.string.isRequired,
  iconFamily: PropTypes.string,
  iconTransform: PropTypes.string,
  keyField: PropTypes.string,
  row: PropTypes.object.isRequired,
  showLabel: PropTypes.bool,
  target: PropTypes.string,
  title: PropTypes.string,
  tooltip: PropTypes.string,
  type: PropTypes.string
};

ActionButton.defaultProps = {
  keyField: 'id'
};

export default ActionButton;
